import type {DocumentReference} from 'firebase/firestore'
import {collection, doc, serverTimestamp, setDoc, type Timestamp} from 'firebase/firestore'
import type {Course} from '@goschool/model'
import {useUserContext} from '@goschool/auth'
import {typeConverter, useFirestore, useFirestoreResults} from '@goschool/react-firebase'
import {useCallback, useMemo} from 'react'

export type MSLQSurveyResponseStatus = 'started' | 'completed' | 'evaluated'

export interface MSLQSurveyResponse {
  created_at: Timestamp;
  completed_at?: Timestamp | null;
  course: DocumentReference<Course>;
  status: MSLQSurveyResponseStatus;
  answers: MSLQAnswer[];
  evaluation?: MSLQEvaluation | null;
  narrative?: MSLQNarrative | null;
}


export function useMSLQDao() {
  const {user} = useUserContext()
  const firestore = useFirestore()
  const surveyCollection = useMemo(
    () => {
      if (user == null) {
        return null
      }
      const userReference = doc(firestore, 'users', user.uid)
      return collection(userReference, 'surveys', 'mslq', 'responses').withConverter(typeConverter<MSLQSurveyResponse>())
    },
    [firestore, user]
  )

  const responses = useFirestoreResults(surveyCollection)

  const started = useMemo(() => {
    return responses?.filter((response) => response.data().status === 'started')
  }, [responses])

  const create = useCallback(
    async (course: DocumentReference<Course>) => {
      if (surveyCollection == null) {
        return null
      }
      const responseRef = doc(surveyCollection)
      const response = {
        created_at: serverTimestamp(),
        course,
        status: 'started' as MSLQSurveyResponseStatus,
        answers: []
      }
      await setDoc(responseRef, response)
      return responseRef
    },
    [surveyCollection]
  )

  return {responses, started, create}
}

export interface MSLQScaleResult {
  scale_id: string;
  score: number;
  z_score: number;
  normalized_score: number;
  relative_score: number;
  score_std: number;
  num_answers: number;
}

export interface MSLQScaleNarrative {
  scale_id: string;
  narrative: string;
}

export interface MSLQNarrative {
  overall_narrative?: string;
  key_points?: string[];
  scale_narratives?: MSLQScaleNarrative[];
}

export interface MSLQEvaluation {
  scale_results: MSLQScaleResult[];
  overall_mean: number;
  overall_std: number;
}

export interface MSLQScale {
  id: string;
  name: string;
  description: string;
  type: 'motivation' | 'learning-strategy';
  group: string;
}

export interface MSLQQuestion {
  id: number;
  scale_id: string;
  text: string;
  reversed?: boolean;
}

export interface MSLQAnswer {
  scale_id: string;
  question_id: number;
  value: number;
}

